import { useState, type FormEvent, type ReactNode } from "react";

import type {
  SchoolworkProgressSnapshot,
  UnderstandingResponse,
} from "@vsta/contracts";

import type { FocusBlockConnectivity } from "./focus-block.js";

export type SchoolworkUiCommand =
  | {
      intent: "done" | "continue" | "need-help";
      stepId: string;
      expectedRevision: number;
      expectedWorkStateRevision: number;
    }
  | {
      intent: "split-step";
      stepId: string;
      expectedRevision: number;
      expectedWorkStateRevision: number;
      children: Array<{
        id: string;
        actionVerb: string;
        instruction: string;
        completionCondition: string;
      }>;
      nextChildStepId: string;
    }
  | {
      intent: "record-understanding";
      stepId: string;
      expectedRevision: number;
      response: UnderstandingResponse;
    };

export type SubmissionEvidenceUiCommand = {
  assignmentId: string;
  expectedRevision: number;
  sourceDescription: string;
  confidence: "confirmed" | "reported";
};

const connected = (value: FocusBlockConnectivity) => value === "connected";

export function StudentOutcomeControls({
  connectivity,
  progress,
  stepId,
  workStateRevision,
  onCommand,
}: {
  connectivity: FocusBlockConnectivity;
  progress: SchoolworkProgressSnapshot;
  stepId: string;
  workStateRevision: number;
  onCommand: (
    command: SchoolworkUiCommand,
  ) => Promise<SchoolworkProgressSnapshot>;
}) {
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [splitting, setSplitting] = useState(false);

  async function run(intent: "done" | "continue" | "need-help") {
    setPending(true);
    setMessage(null);
    setError(null);
    try {
      await onCommand({
        intent,
        stepId,
        expectedRevision: progress.revision,
        expectedWorkStateRevision: workStateRevision,
      });
      setMessage(
        intent === "done"
          ? "Done recorded. The Assignment status was recalculated."
          : intent === "continue"
            ? "Continue recorded. This Step remains active."
            : "Help requested. This Step and its history are preserved.",
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "This outcome was not confirmed. Nothing was silently changed.",
      );
    } finally {
      setPending(false);
    }
  }

  async function submitSplit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const ids = [crypto.randomUUID(), crypto.randomUUID()];
    const children = ids.map((id, index) => ({
      id,
      actionVerb: String(data.get(`child-${index}-verb`) ?? ""),
      instruction: String(data.get(`child-${index}-instruction`) ?? ""),
      completionCondition: String(data.get(`child-${index}-condition`) ?? ""),
    }));
    const selected = Number(data.get("next-child") ?? 0);
    setPending(true);
    setMessage(null);
    setError(null);
    try {
      await onCommand({
        intent: "split-step",
        stepId,
        expectedRevision: progress.revision,
        expectedWorkStateRevision: workStateRevision,
        children,
        nextChildStepId: ids[selected] ?? ids[0]!,
      });
      setMessage(
        "Step split recorded. The selected smaller Step is now active.",
      );
      setSplitting(false);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "The Step split was not confirmed. Your entries remain here.",
      );
    } finally {
      setPending(false);
    }
  }

  const unavailable = pending || !connected(connectivity);
  return (
    <section className="outcome-panel" aria-labelledby={`outcome-${stepId}`}>
      <p className="status-label">Block-end choices</p>
      <h3 id={`outcome-${stepId}`}>What happens next?</h3>
      <p>
        Choose what is true now. Done records this Step only; school submission
        is verified separately when required.
      </p>
      {!connected(connectivity) ? (
        <p className="connectivity-note" role="status">
          Reconnect before recording an outcome so it is not mistaken for an
          accepted change.
        </p>
      ) : null}
      <div className="outcome-actions">
        <button
          type="button"
          disabled={unavailable}
          onClick={() => void run("done")}
        >
          Done
        </button>
        <button
          className="secondary-button"
          type="button"
          disabled={unavailable}
          onClick={() => void run("continue")}
        >
          Continue
        </button>
        <button
          className="secondary-button"
          type="button"
          disabled={unavailable}
          onClick={() => void run("need-help")}
        >
          I’m stuck
        </button>
        <button
          className="secondary-button"
          type="button"
          disabled={unavailable}
          aria-expanded={splitting}
          onClick={() => setSplitting((value) => !value)}
        >
          Split Step
        </button>
      </div>
      {splitting ? (
        <form
          className="split-step-form"
          onSubmit={(event) => void submitSplit(event)}
        >
          <fieldset disabled={pending}>
            <legend>Make two smaller observable Steps</legend>
            {[0, 1].map((index) => (
              <div className="split-child" key={index}>
                <h4>Smaller Step {index + 1}</h4>
                <label htmlFor={`child-${stepId}-${index}-verb`}>
                  Action word
                </label>
                <input
                  id={`child-${stepId}-${index}-verb`}
                  name={`child-${index}-verb`}
                  required
                />
                <label htmlFor={`child-${stepId}-${index}-instruction`}>
                  Step instruction
                </label>
                <input
                  id={`child-${stepId}-${index}-instruction`}
                  name={`child-${index}-instruction`}
                  required
                />
                <label htmlFor={`child-${stepId}-${index}-condition`}>
                  Done means
                </label>
                <input
                  id={`child-${stepId}-${index}-condition`}
                  name={`child-${index}-condition`}
                  required
                />
                <label className="radio-choice">
                  <input
                    type="radio"
                    name="next-child"
                    value={index}
                    defaultChecked={index === 0}
                  />
                  Start this smaller Step next
                </label>
              </div>
            ))}
          </fieldset>
          <div className="outcome-actions">
            <button type="submit" disabled={pending}>
              Save and start smaller Step
            </button>
            <button
              className="secondary-button"
              type="button"
              disabled={pending}
              onClick={() => setSplitting(false)}
            >
              Cancel split
            </button>
          </div>
        </form>
      ) : null}
      {message === null ? null : <p role="status">{message}</p>}
      {error === null ? null : <p role="alert">{error}</p>}
    </section>
  );
}

const understandingLabels: Array<[UnderstandingResponse, string]> = [
  ["need-help", "Need Help"],
  ["still-shaky", "Still Shaky"],
  ["mostly-understand", "Mostly Understand"],
  ["could-explain-it", "Could Explain It"],
];

export function UnderstandingChecks({
  connectivity,
  progress,
  onCommand,
}: {
  connectivity: FocusBlockConnectivity;
  progress: SchoolworkProgressSnapshot;
  onCommand: (
    command: SchoolworkUiCommand,
  ) => Promise<SchoolworkProgressSnapshot>;
}) {
  const eligible = progress.steps.find(
    ({ understandingCheckEligible }) => understandingCheckEligible,
  );
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (eligible === undefined) return null;
  return (
    <section
      className="understanding-panel"
      aria-labelledby="understanding-title"
    >
      <p className="status-label">Optional reflection</p>
      <h2 id="understanding-title">Understanding Check</h2>
      <p>
        After this learning Step, which description fits best? This guides
        support; it is not a grade.
      </p>
      <p className="done-means">{eligible.instruction}</p>
      <div className="understanding-actions">
        {understandingLabels.map(([response, label]) => (
          <button
            className="secondary-button"
            type="button"
            key={response}
            disabled={pending || !connected(connectivity)}
            onClick={() => {
              setPending(true);
              setMessage(null);
              setError(null);
              void onCommand({
                intent: "record-understanding",
                stepId: eligible.id,
                response,
                expectedRevision: progress.revision,
              })
                .then(() => setMessage("Understanding Check recorded."))
                .catch((reason: unknown) =>
                  setError(
                    reason instanceof Error
                      ? reason.message
                      : "The Understanding Check was not confirmed.",
                  ),
                )
                .finally(() => setPending(false));
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {message === null ? null : <p role="status">{message}</p>}
      {error === null ? null : <p role="alert">{error}</p>}
    </section>
  );
}

const assignmentStatusLabel: Record<
  SchoolworkProgressSnapshot["assignments"][number]["status"],
  string
> = {
  unplanned: "Unplanned",
  planned: "Planned",
  "in-progress": "In Progress",
  "ready-to-verify": "Ready to Verify",
  completed: "Completed",
  deferred: "Deferred",
  cancelled: "Cancelled",
};

const actorLabel = (role: "parent" | "student") =>
  role === "parent" ? "Parent" : "Student";

const outcomeLabel = (
  intent: SchoolworkProgressSnapshot["outcomes"][number]["intent"],
) =>
  ({
    done: "marked Done",
    continue: "chose Continue",
    "need-help": "asked for help",
    "split-step": "split the Step into smaller Steps",
    skip: "skipped the Step with parent approval",
  })[intent];

function historyTime(at: string) {
  return (
    <time dateTime={at}>
      {new Date(at).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })}
    </time>
  );
}

function countSummary(
  label: "Required" | "Optional",
  counts: SchoolworkProgressSnapshot["assignments"][number]["required"],
) {
  return `${label}: ${counts.total} total · ${counts.done} Done · ${counts.remaining} Remaining · ${counts.blocked} Blocked · ${counts.deferred} Deferred · ${counts.skipped} Skipped`;
}

export function AssignmentProgress({
  role,
  connectivity,
  progress,
  onEvidence,
}: {
  role: "parent" | "student";
  connectivity: FocusBlockConnectivity;
  progress: SchoolworkProgressSnapshot;
  onEvidence?: (
    command: SubmissionEvidenceUiCommand,
  ) => Promise<SchoolworkProgressSnapshot>;
}) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stepById = new Map(progress.steps.map((step) => [step.id, step]));
  const assignmentById = new Map(
    progress.assignments.map((assignment) => [assignment.id, assignment]),
  );
  const history: Array<{ id: string; at: string; content: ReactNode }> = [
    ...progress.outcomes.map((outcome) => ({
      id: `outcome-${outcome.id}`,
      at: outcome.occurredAt,
      content: (
        <>
          <strong>{actorLabel(outcome.actor.role)}</strong>{" "}
          {outcomeLabel(outcome.intent)}:{" "}
          {stepById.get(outcome.stepId)?.instruction ?? "Step"}.
        </>
      ),
    })),
    ...progress.helpRequests.map((request) => ({
      id: `help-${request.id}`,
      at: request.createdAt,
      content: (
        <>
          <strong>{actorLabel(request.actor.role)}</strong> opened a help
          request for {stepById.get(request.stepId)?.instruction ?? "Step"}.
          Status: {request.status === "open" ? "Open" : "Resolved"}.
        </>
      ),
    })),
    ...progress.understandingChecks.map((check) => ({
      id: `understanding-${check.id}`,
      at: check.recordedAt,
      content: (
        <>
          <strong>{actorLabel(check.actor.role)}</strong> recorded an optional
          Understanding Check (
          {understandingLabels.find(
            ([value]) => value === check.response,
          )?.[1] ?? check.response}
          ) for {stepById.get(check.stepId)?.instruction ?? "Step"}.
        </>
      ),
    })),
    ...progress.submissionEvidence.map((evidence) => ({
      id: `evidence-${evidence.id}`,
      at: evidence.recordedAt,
      content: (
        <>
          <strong>{actorLabel(evidence.actor.role)}</strong> added Submission
          Evidence for{" "}
          {assignmentById.get(evidence.assignmentId)?.title ?? "Assignment"}:{" "}
          {evidence.sourceDescription} ({evidence.confidence}).
        </>
      ),
    })),
  ].sort((left, right) => left.at.localeCompare(right.at));
  return (
    <section
      className="assignment-progress"
      aria-labelledby="assignment-progress-title"
    >
      <h2 id="assignment-progress-title">Assignment progress</h2>
      <ul>
        {progress.assignments.map((assignment) => (
          <li key={assignment.id}>
            <div className="assignment-progress-heading">
              <h3>{assignment.title}</h3>
              <span className="assignment-status">
                {assignmentStatusLabel[assignment.status]}
              </span>
            </div>
            <p>{countSummary("Required", assignment.required)}</p>
            <p>{countSummary("Optional", assignment.optional)}</p>
            {assignment.status === "ready-to-verify" ? (
              <p>
                Required work is Done, but this Assignment is not Completed
                until Submission Evidence is added.
              </p>
            ) : null}
            {role === "parent" &&
            assignment.status === "ready-to-verify" &&
            onEvidence !== undefined ? (
              <form
                className="evidence-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  const data = new FormData(event.currentTarget);
                  const sourceDescription = String(
                    data.get("sourceDescription") ?? "",
                  );
                  setPendingId(assignment.id);
                  setMessage(null);
                  setError(null);
                  void onEvidence({
                    assignmentId: assignment.id,
                    expectedRevision: progress.revision,
                    sourceDescription,
                    confidence: "confirmed",
                  })
                    .then(() =>
                      setMessage(
                        "Submission Evidence added. Assignment Completed.",
                      ),
                    )
                    .catch((reason: unknown) =>
                      setError(
                        reason instanceof Error
                          ? reason.message
                          : "Submission Evidence was not confirmed.",
                      ),
                    )
                    .finally(() => setPendingId(null));
                }}
              >
                <label htmlFor={`evidence-${assignment.id}`}>
                  How submission was verified
                </label>
                <input
                  id={`evidence-${assignment.id}`}
                  name="sourceDescription"
                  required
                  maxLength={500}
                />
                {connected(connectivity) ? null : (
                  <p className="connectivity-note" role="status">
                    {connectivity === "offline"
                      ? "Offline · reconnect before adding Submission Evidence. Your verification entry remains available."
                      : "Retrying live sync · wait before adding Submission Evidence so the latest Assignment status can be confirmed."}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={pendingId !== null || !connected(connectivity)}
                >
                  {pendingId === assignment.id
                    ? "Adding evidence…"
                    : "Add Submission Evidence"}
                </button>
              </form>
            ) : null}
          </li>
        ))}
      </ul>
      <section
        className="schoolwork-history"
        aria-labelledby="schoolwork-history-title"
      >
        <h3 id="schoolwork-history-title">Activity history</h3>
        {history.length === 0 ? (
          <p>No outcomes or checks have been recorded yet.</p>
        ) : (
          <ol>
            {history.map((entry) => (
              <li key={entry.id}>
                <p>{entry.content}</p>
                <p className="history-time">{historyTime(entry.at)}</p>
              </li>
            ))}
          </ol>
        )}
      </section>
      {message === null ? null : <p role="status">{message}</p>}
      {error === null ? null : <p role="alert">{error}</p>}
    </section>
  );
}
